/**
 * FİNANS — yalnızca `boss` + `financeView`.
 *
 * ── EKRANIN SÖZÜ ───────────────────────────────────────────────────────────
 * Bu sayfadaki hiçbir rakam "tahmin edilmiş bir sıfır" değildir:
 *  · Maliyeti bilinmeyen kalem 0 sayılmaz; kaç kalem olduğu ve niçin bilinmediği
 *    kırmızı/sarı bir uyarı olarak YAZILIR. Aksi hâlde kâr olduğundan yüksek
 *    görünür ve bu, bu projede defalarca yaşanmış en pahalı hata sınıfıdır.
 *  · Sipariş listesi sınıra dayanırsa `ListLimitNotice` bunu söyler.
 *  · SSH gideri tanımlanmamışsa "0 TL" değil "tanımlanmamış" yazar.
 *  · Kârlılık özeti KDV, komisyonu ve kargo maliyetini ANA HESABA KATAR
 *    (iş sahibi kararı, 2026-08-21). Üçü de ayrı satır olarak görünür, sonra
 *    Toplam Gider'e girer; eski "hesaba dâhil olmayanlar" bloğu kaldırıldı.
 *    Bu satır 2026-08-30'a kadar tersini söylüyordu — metin eski davranıştan
 *    kalmıştı ve iş sahibini kendi rakamı hakkında yanıltıyordu.
 *
 * ── YETKİ ──────────────────────────────────────────────────────────────────
 * Menü ve rota `boss` dışına gösterilmez, uç noktalar `assertRole(["boss"])` +
 * `assertFinanceView` arar, `firestore.rules` finans koleksiyonlarını
 * `financeView` ile kilitler. Yalnızca arayüzde gizlemek eski sistemin G-08
 * açığıydı (`ajax_financialdashboard.php` herkese açıktı).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  PageHeader,
  Modal,
  Field,
  MoneyInput,
  EmptyState,
  ErrorNote,
  DataTable,
} from "../components";
// `Column` tipi barrel'da dışa aktarılmıyor; doğrudan kaynağından alınır.
import type { Column } from "../components/DataTable";
import { ListLimitNotice } from "../components/ListLimitNotice";
import { Collapsible } from "../components/Collapsible";
import { LoadingBlock } from "../components/Spinner";
import {
  DateRangePicker,
  type RangeValue,
} from "../components/DateRangePicker";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useCallableAction, useDoc } from "../hooks";
import { db, auth } from "../lib/firebase";
import { tl, n, pct } from "../lib/format";
import {
  parseKurus,
  formatKurusInput,
  parseDecimal,
  formatDecimalInput,
} from "../lib/decimal";
import { DecimalInput } from "../components/DecimalInput";
import { csvIndir, csvMetni, csvSayi, csvTutar, dosyaParcasi } from "../lib/csv";

/* ══════════════════════════════════════════════════════════════════════════
 * SUNUCU SÖZLEŞMESİ — `functions/src/finance/dashboard.ts` ile BİREBİR
 *
 * ★ ALAN ADLARI SUNUCUYLA AYNI OLMAK ZORUNDA. Bu projede en sık yaşanan hata
 * sınıfı, bir yerde yazılan adın başka yerde farklı okunmasıydı: Firestore ve
 * callable HATA VERMEZ, alan sessizce `undefined` gelir ve ekran boş kalır.
 * ═══════════════════════════════════════════════════════════════════════ */

type AdChannel = "meta" | "google" | "other";
type ExpenseCategory = "personnel" | "operating" | "custom";
type UncostedReason = "unmatched" | "noCost" | "badQuantity";

interface SoldProductRow {
  productId: string;
  title: string;
  sku: string;
  quantity: number;
  unitMaterialKurus: number | null;
  totalMaterialKurus: number | null;
  invoiceKurus: number | null;
  totalCargoKurus: number | null;
  estimatedCargoQuantity: number;
  reason?: UncostedReason;
}

interface AdRow {
  id: string;
  dateKey: string;
  channel: AdChannel;
  label: string;
  amountKurus: number;
  source: string;
  note: string;
}

interface ExpenseRow {
  id: string;
  dateKey: string;
  category: ExpenseCategory;
  label: string;
  amountKurus: number;
  note: string;
}

/** Sunucudaki `finance/revenue.ts:RevenueBreakdown` ile aynı şekil. */
interface RevenueBreakdown {
  counted: {
    orderCount: number;
    zeroOrderCount: number;
    grossKurus: number;
    discountCodeKurus: number;
    trendyolDiscountKurus: number;
    refundKurus: number;
    havaleKurus: number;
    netKurus: number;
  };
  /** İşbirliği siparişleri — normal cirodan AYRI. */
  collab?: { orderCount: number; listKurus: number; netKurus: number };
  byChannel: Array<{ channel: string; label: string; orderCount: number; netKurus: number }>;
  excluded: Array<{ group: string; label: string; orderCount: number; amountKurus: number }>;
  exceptions: Array<{ id: string; orderName: string; amountKurus: number; reason: string }>;
}

interface FinanceResponse {
  preset: string;
  presetLabel: string;
  rangeLabel: string;
  days: number;
  settings: {
    invoiceMultiplier: number;
    kdv: number;
    komisyon: number;
    shopifyKomisyon: number;
    /** Trendyol net komisyonu (%16,1) — yalnızca Trendyol cirosuna uygulanır. */
    trendyolKomisyon?: number;
  };
  revenue: {
    measurable: boolean;
    kurus: number;
    orderCount: number;
    matchedOrderCount: number;
    truncated: boolean;
    limit: number;
    basis: string;
    /** Tutarı > 0 olan sayılan sipariş (İşbirliği vb. 0 TL'likler hariç). */
    paidOrderCount?: number | null;
    breakdown?: RevenueBreakdown | null;
    breakdownTruncated?: boolean;
    breakdownLimit?: number;
    breakdownMismatch?: boolean;
  };
  /**
   * İşbirliği için gönderilen ürünler — ciroya 0 ₺ yazar; maliyetleri satış
   * maliyetinden ve "Kesilecek Fatura"dan AYRI tutulur.
   */
  collab?: {
    orderCount: number;
    listKurus: number;
    netKurus: number;
    materialKurus: number;
    cargoKurus: number;
    invoiceKurus: number;
    costedItems: number;
    uncostedItems: number;
    estimatedCargoQuantity: number;
    rows: SoldProductRow[];
  };
  soldProducts: {
    rows: SoldProductRow[];
    materialKurus: number;
    cargoKurus: number;
    invoiceKurus: number;
    costedItems: number;
    uncostedItems: number;
    uncostedQuantity: number;
    reasons: Record<UncostedReason, number>;
    estimatedCargoQuantity: number;
    cargoEstimatePercent: number;
  };
  ads: {
    total: number;
    byChannel: Array<{ channel: AdChannel; label: string; amountKurus: number; count: number }>;
    rows: AdRow[];
    /** Sınıra dayanıldı mı — dayanıldıysa TOPLAM EKSİKTİR, ekran yazmak zorunda. */
    truncated: boolean;
    limit: number;
    /** Seçili aralığın DIŞINDA kalan kayıt sayısı ve en yenisinin tarihi. */
    otherCount: number;
    latestDateKey: string;
  };
  expenses: {
    byCategory: Array<{
      category: ExpenseCategory;
      label: string;
      amountKurus: number;
      count: number;
    }>;
    rows: ExpenseRow[];
    /** Sınıra dayanıldı mı — dayanıldıysa TOPLAM EKSİKTİR, ekran yazmak zorunda. */
    truncated: boolean;
    limit: number;
    otherCount: number;
    latestDateKey: string;
  };
  /** SSH gideri — TAHMİN DEĞİL, gerçek `afterSales` kayıtlarının tutar toplamı. */
  ssh: {
    kurus: number;
    basis: string;
    recordCount: number;
    costedCount: number;
    /** ★ Tutarı girilmemiş kayıt sayısı — ekran AÇIKÇA yazmak zorunda. */
    uncostedCount: number;
    /** Sınıra dayanıldı mı — dayanıldıysa TOPLAM EKSİKTİR, ekran yazmak zorunda. */
    truncated: boolean;
    limit: number;
    items: SshCostItem[];
    dateField: string;
    dateFieldLabel: string;
  };
  summary: {
    revenueKurus: number;
    materialKurus: number;
    /** Kargo, KDV ve komisyon ARTIK ana toplamın içinde (iş sahibi kararı). */
    cargoKurus: number;
    kdvKurus: number;
    commissionKurus: number;
    /** Kesintinin Trendyol payı ve o payın cirosu. */
    trendyolCommissionKurus?: number;
    trendyolRevenueKurus?: number;
    adKurus: number;
    personnelKurus: number;
    operatingKurus: number;
    customKurus: number;
    sshKurus: number;
    /** İşbirliği ürünlerinin malzeme + kargo maliyeti — ayrı gider satırı. */
    isbirligiKurus?: number;
    totalExpenseKurus: number;
    netProfitKurus: number;
    marginPercent: number | null;
  };
  /** Kâr oranının tabanı — ekranda yazdırılır. */
  basisLabel: string;
  netRevenueKurus: number;
}

interface SshCostItem {
  sshId: string;
  dateKey: string;
  productTitle: string;
  orderName: string;
  quantity: number;
  problemNote: string;
  status: string;
  statusLabel: string;
  /** **`null` = tutar girilmemiş** — 0 TL ile aynı şey değil. */
  costKurus: number | null;
}

const UNCOSTED_REASON_LABELS: Record<UncostedReason, string> = {
  unmatched: "katalog ürününe bağlanamadı",
  noCost: "malzeme maliyeti hesaplanmamış",
  badQuantity: "kalem adedi yok / geçersiz",
};

const AD_CHANNEL_OPTIONS: Array<{ value: AdChannel; label: string }> = [
  { value: "meta", label: "Meta Ads" },
  { value: "google", label: "Google Ads" },
  { value: "other", label: "Diğer" },
];

const EXPENSE_CATEGORY_OPTIONS: Array<{ value: ExpenseCategory; label: string }> = [
  { value: "personnel", label: "Personel Giderleri" },
  { value: "operating", label: "Genel İşletme Giderleri" },
  { value: "custom", label: "Özel Giderler" },
];

/**
 * Hazır kalem ADLARI — öneri, zorunluluk değil.
 * Kapalı bir liste, listede olmayan gideri "Diğer"e iterek raporu körleştirirdi.
 */
const EXPENSE_SUGGESTIONS: Record<ExpenseCategory, string[]> = {
  personnel: ["Maaşlar", "SGK / Sigorta", "Tazminatlar", "Yemek"],
  operating: ["Elektrik", "Su", "Doğalgaz", "Kira", "Muhasebe", "İnternet / Telefon"],
  custom: ["Influencer", "Fotoğraf / Video", "Danışmanlık", "Fuar", "Yazılım"],
};

/** Seçilen aralık oturumlar arası korunur — Dashboard ile aynı desen. */
const STORAGE_KEY = "balder.finance.range";

function loadRange(): RangeValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw) as RangeValue;
      // ★ TARİHSİZ «Özel» KAYDI YOK SAYILIR. Seçici, kullanıcı «Özel»e basar
      // basmaz (tarihleri girmeden) `{preset:"custom"}` yayınlıyor ve bu hâliyle
      // depoya yazılıyordu. Sunucu tarafında `resolveRange` bunu reddediyor
      // ("Özel aralık için başlangıç ve bitiş tarihi gerekli"), callable null
      // dönüyor, `data` hiç dolmuyor ve SAYFA GÖVDESİ BOŞ AÇILIYORDU — üstelik
      // depoya yazıldığı için sonraki oturumlarda da. Bozuk kaydı okumamak,
      // yazılmasını engellemekten daha ucuz bir güvenlik ağı.
      if (v && typeof v.preset === "string") {
        if (v.preset !== "custom" || (v.from && v.to)) return v;
      }
    }
  } catch {
    // Bozuk kayıt ekranın açılmasını engellemez.
  }
  return { preset: "this_month" };
}

/**
 * "Kayıt yok" ile "BU ARALIKTA kayıt yok" ayrımını cümleye döker.
 *
 * ★ NİÇİN: `Son 7/14/28/30 gün` ön ayarları BUGÜNÜ HARİÇ TUTAR (Shopify
 * tanımı). Bugün girilen gider o aralıklarda görünmez ve ekran "girilmemiş"
 * derdi — doğru ama yanıltıcı bir cümle; kullanıcı kaydın kaybolduğunu sanır.
 * Canlıda tam olarak bu yaşandı: 380.000 ₺ reklam ve 1.000.000 ₺ personel
 * gideri kayıtlıyken ekran "girilmemiş" diyordu.
 */
function outsideHint(
  s: { otherCount: number; latestDateKey: string },
  what: string,
): string | null {
  if (s.otherCount <= 0) return null;
  const tarih = s.latestDateKey ? trDate(s.latestDateKey) : "";
  return (
    `Sistemde ${s.otherCount} ${what} kaydı var ama seçili tarih aralığının dışında` +
    (tarih ? ` (en yenisi ${tarih}).` : ".") +
    " Üstteki tarih aralığını değiştirin — «Son 30 gün» gibi ön ayarlar BUGÜNÜ kapsamaz."
  );
}

/** "2026-08-21" → "21.08.2026". */
function trDate(key: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : key;
}

/** Bugünün "yyyy-aa-gg" biçimi — gider formunun varsayılan tarihi. */
function todayIso(): string {
  const d = new Date();
  const z = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * KÜÇÜK BİLEŞENLER
 * ═══════════════════════════════════════════════════════════════════════ */

function StatCard({
  label,
  value,
  basis,
  tone = "normal",
}: {
  label: string;
  value: string;
  basis?: string;
  /** `muted` = ölçülemedi / tanımsız; sayı gibi basılmaz. */
  tone?: "normal" | "muted" | "good" | "bad";
}) {
  const valueClass =
    tone === "muted"
      ? "mt-1 text-base font-medium italic tracking-tight text-amber-700"
      : tone === "good"
        ? "mt-1 text-[26px] font-bold tracking-tight text-emerald-600 tabular-nums"
        : tone === "bad"
          ? "mt-1 text-[26px] font-bold tracking-tight text-rose-600 tabular-nums"
          : "mt-1 text-[26px] font-bold tracking-tight text-gray-950 tabular-nums";

  return (
    <div className="card card-pad">
      <dt className="truncate text-sm font-medium text-gray-500">{label}</dt>
      <dd className={valueClass}>{value}</dd>
      {/* ★ HER RAKAMIN TABANI YAZAR. "%55,5" tek başına, neyin yüzdesi olduğu
          söylenmeden yanıltıcıdır. */}
      {basis ? <p className="mt-1 text-xs leading-snug text-gray-500">{basis}</p> : null}
    </div>
  );
}

/**
 * CİRO DÖKÜMÜ — "gerçek ciroyu göremiyorum" şikâyetinin cevabı.
 *
 * Havale, iade ve indirim zaten düşülüyordu ama TEK rakamın içinde
 * görünmüyordu. Burada brütten nete her kalem ayrı satır; sayılmayan
 * siparişler nedenleriyle; karar isteyen siparişler bağlantısıyla.
 */
function RevenueBreakdownSection({
  b,
  kdv,
  totalKurus,
  truncated,
  limit,
  mismatch,
}: {
  b: RevenueBreakdown;
  kdv: number;
  totalKurus: number;
  truncated: boolean;
  limit: number;
  mismatch: boolean;
}) {
  const c = b.counted;
  const eksi = (k: number) => (k > 0 ? `−${tl(k)}` : tl(0));
  const satir = (label: string, value: string, hint?: string, strong = false) => (
    <div className={`flex items-baseline justify-between gap-4 py-1.5 ${strong ? "border-t border-gray-300 font-semibold text-gray-900" : "text-gray-700"}`}>
      <dt className="min-w-0">
        {label}
        {hint ? <span className="ml-2 text-xs font-normal text-gray-500">{hint}</span> : null}
      </dt>
      <dd className="shrink-0 tabular-nums">{value}</dd>
    </div>
  );

  return (
    <Collapsible
      storageKey="finans.ciroDokum"
      title="Ciro dökümü — brütten gerçek ciroya"
      meta={`brüt ${tl(c.grossKurus)} → gerçek ciro ${tl(c.netKurus)} · ${n(c.orderCount, " sipariş")}`}
    >
      <p className="text-xs text-gray-500">
        Sipariş tarihine göre. Havale indirimi Shopify'a girmediği için burada düşülür; iade ve
        indirim kodları Shopify tutarından gelir.
      </p>

      {mismatch ? (
        <p role="alert" className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
          Döküm, yukarıdaki Toplam Ciro ile tutmuyor — lütfen bildirin. (Döküm {tl(c.netKurus)}, ciro {tl(totalKurus)})
        </p>
      ) : null}
      {truncated ? (
        <p role="status" className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Bu aralık sipariş sınırına dayandı (döküm en yeni {n(limit)} sipariş). Aşağıdaki
          rakamlar KISMİDİR ve Toplam Ciro ile karşılaştırılamaz — dönemi daraltın.
        </p>
      ) : null}

      <div className="mt-3 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <dl className="text-sm">
          {satir("Brüt satış (liste fiyatı)", tl(c.grossKurus), `${n(c.orderCount, " sipariş")}`)}
          {satir("İndirim kodları", eksi(c.discountCodeKurus))}
          {c.trendyolDiscountKurus > 0 ? satir("Trendyol satıcı indirimi", eksi(c.trendyolDiscountKurus)) : null}
          {satir("Kısmi iadeler", eksi(c.refundKurus), "tamamen iade edilenler: Ciroya sayılmayanlar")}
          {satir("Havale indirimi", eksi(c.havaleKurus))}
          {satir("Gerçek ciro (KDV dahil)", tl(c.netKurus), undefined, true)}
          {satir(
            "Gerçek ciro (KDV hariç)",
            tl(Math.round(c.netKurus / (1 + kdv / 100))),
            `KDV %${kdv}`,
          )}
          {c.zeroOrderCount > 0 ? (
            <p className="mt-2 text-xs text-gray-500">
              {n(c.zeroOrderCount)} sipariş tutarı 0 olduğu için ödenmiş sipariş sayısına katılmadı
              (tamamen iade vb.).
            </p>
          ) : null}
          {b.collab && b.collab.orderCount > 0 ? (
            <p className="mt-2 text-xs text-gray-500">
              {n(b.collab.orderCount)} işbirliği siparişi bu dökümde YOK — aşağıda ayrı gösterilir.
              {b.collab.netKurus > 0
                ? ` Bu siparişlerde tahsil edilen ${tl(b.collab.netKurus)} Toplam Ciro'ya dâhildir.`
                : ""}
            </p>
          ) : null}
        </dl>

        <div className="space-y-4 text-sm">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Kanala göre</h3>
            <dl className="mt-1">
              {b.byChannel.map((k) => (
                <div key={k.channel} className="flex items-baseline justify-between gap-4 py-1 text-gray-700">
                  <dt>
                    {k.label} <span className="text-xs text-gray-500">({n(k.orderCount)})</span>
                  </dt>
                  <dd className="tabular-nums">{tl(k.netKurus)}</dd>
                </div>
              ))}
            </dl>
          </div>

          {b.excluded.length > 0 ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ciroya sayılmayanlar
              </h3>
              <dl className="mt-1">
                {b.excluded.map((e) => (
                  <div key={e.group} className="flex items-baseline justify-between gap-4 py-1 text-gray-600">
                    <dt>
                      {e.label} <span className="text-xs text-gray-500">({n(e.orderCount)})</span>
                    </dt>
                    <dd className="tabular-nums">{tl(e.amountKurus)}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-1 text-xs text-gray-500">Tutarlar sipariş tutarıdır; ciroda hiç yer almazlar.</p>
            </div>
          ) : null}
        </div>
      </div>

      {b.exceptions.length > 0 ? (
        <div className="mt-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-semibold">{n(b.exceptions.length)} sipariş kontrol istiyor</p>
          <ul role="list" className="mt-1 space-y-1">
            {b.exceptions.map((x) => (
              <li key={x.id} className="flex flex-wrap items-baseline gap-x-2">
                <Link to={`/orders/${x.id}`} className="font-mono text-xs underline">
                  {x.orderName}
                </Link>
                <span className="tabular-nums">{tl(x.amountKurus)}</span>
                <span className="text-xs">— {x.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Collapsible>
  );
}

/** Özet tablosunun bir satırı. */
function SummaryLine({
  label,
  value,
  hint,
  strong,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
  tone?: "good" | "bad";
}) {
  const color = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-700" : "";
  return (
    <div
      className={`flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 ${
        strong ? "bg-gray-50 font-semibold" : ""
      }`}
    >
      <div className="min-w-0">
        <span className={strong ? "text-gray-900" : "text-gray-700"}>{label}</span>
        {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
      </div>
      <span className={`shrink-0 tabular-nums ${color || "text-gray-900"}`}>{value}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * FİNANS PARAMETRELERİ — `settings/finance`
 * ═══════════════════════════════════════════════════════════════════════ */

/** Finans sekmesinin `settings/finance` içinde kullandığı tek parametre. */
interface FinanceParamDoc {
  invoiceMultiplier?: number;
}

/**
 * Fatura çarpanı — ekrandan düzenlenir.
 *
 * ── ★ SSH PARAMETRELERİ BURADAN KALDIRILDI ─────────────────────────────────
 * Önceki sürümde bu panel SSH giderini de ayarlıyordu (sabit aylık tutar ya da
 * ciro yüzdesi). İş sahibinin kararı (2026-08-21): "SSH panelimiz var, oraya
 * tutar gireriz ürün bazlı." SSH gideri artık gerçek `afterSales` kayıtlarının
 * tutar toplamıdır ve tutarlar SSH ekranından girilir.
 *
 * Ayar KORUNMADI, silindi. İki ayrı SSH gideri kaynağı bırakmak ("ayarda %1,5
 * yazıyor ama panelde 12.400 TL girilmiş — hangisi doğru?") bu projede
 * defalarca yaşanmış hata sınıfının ta kendisi: aynı büyüklüğün iki kaynağı
 * olunca biri düzeltilip diğeri unutuluyor ve iki ekran farklı sayı gösteriyor.
 *
 * ── NİÇİN AYARLAR EKRANINDA DEĞİL, BURADA ──────────────────────────────────
 * Çarpan YALNIZCA bu ekranın rakamını değiştiriyor; ayarını gördüğü yerde
 * tutmak "bu rakam neden böyle?" sorusunu bir tık uzağa indirir. `Ayarlar`
 * ekranındaki finans formu AYRI alanları yazıyor ve `merge` kullandığı için iki
 * form birbirinin alanlarını EZMEZ.
 *
 * ★ DEĞER ARALIĞI ÜÇ YERDE AYNI: burada (kullanıcıya Türkçe mesaj),
 * `firestore.rules` içinde (istemciye karşı) ve `core/settings.ts:pick()`
 * içinde (bozuk veriye karşı). Yalnızca arayüzde doğrulamak yeterli değildir.
 */
function FinanceParams({ onSaved }: { onSaved: () => void }) {
  const { data, loading, error, notFound } = useDoc<FinanceParamDoc>("settings/finance");

  const [open, setOpen] = useState(false);
  const [multiplier, setMultiplier] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setMultiplier(formatDecimalInput(data.invoiceMultiplier ?? 2));
  }, [data]);

  const save = async () => {
    setErr(null);
    setOk(null);

    const m = parseDecimal(multiplier);
    if (m === null || m < 0.01 || m > 100) {
      // ★ Alt sınır 0 DEĞİL: sıfır çarpan her faturayı sessizce 0 TL yapardı.
      setErr("Fatura çarpanı 0,01 ile 100 arasında bir sayı olmalıdır (örnek: 2).");
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    setBusy(true);
    try {
      /**
       * ★ `merge: true` ve YALNIZCA bu alan yazılır.
       * Ayarlar ekranındaki finans formu KDV/komisyon/kargo alanlarını yazıyor;
       * iki form aynı dokümanın farklı alanlarına dokunur, birbirini ezmez.
       *
       * ⚠ Noktalı anahtar KULLANILMAZ — `set({merge:true})` noktalı anahtarı
       * alan YOLU saymaz, "a.b" adında GERÇEK bir alan yaratır (bu projede bir
       * kez yaşandı). Alanların hepsi düz.
       */
      await setDoc(
        doc(db, "settings", "finance"),
        {
          invoiceMultiplier: m,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      // ★ Sunucu ayarları 60 sn önbelleğe alıyor (`core/settings.ts:TTL_MS`).
      // Kullanıcıya "kaydedildi ama rakam değişmedi" dedirtmemek için SÖYLENİR.
      setOk("Kaydedildi. Rakamlar en geç bir dakika içinde yeni çarpanla gelir.");
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kayıt sırasında bilinmeyen bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  if (notFound) {
    return (
      <div
        role="alert"
        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      >
        <strong>Finans ayarları dokümanı yok.</strong> Rakamlar varsayılan fatura
        çarpanıyla (2) hesaplanıyor. Ayarlar ekranından finans parametrelerini bir
        kez kaydedin.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">
          Finans parametresi — fatura çarpanı
        </span>
        <span className="text-sm text-gray-500">{open ? "Gizle" : "Düzenle"}</span>
      </button>

      {open ? (
        <div className="mt-4 space-y-4">
          <ErrorNote error={error} />

          <Field
            label="Fatura çarpanı"
            required
            hint="Kesilecek fatura = toplam malzeme maliyeti × bu sayı. Varsayılan 2."
            htmlFor="fin-carpan"
          >
            <DecimalInput id="fin-carpan" value={multiplier} onChange={setMultiplier} />
          </Field>

          <p className="text-xs text-gray-600">
            SSH gideri buradan ayarlanmaz: gerçek SSH kayıtlarının tutar toplamıdır.
            Tutarlar <strong>SSH ekranından</strong> kayıt bazında girilir.
          </p>

          {err ? <ErrorNote error={err} /> : null}
          {ok ? (
            <p role="status" className="text-sm text-emerald-700">
              {ok}
            </p>
          ) : null}

          <button type="button" className="btn-primary" disabled={busy} onClick={save}>
            {busy ? "Kaydediliyor…" : "Parametreyi kaydet"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * SAYFA
 * ═══════════════════════════════════════════════════════════════════════ */

export function Finance() {
  const [range, setRange] = useState<RangeValue>(loadRange);
  const [data, setData] = useState<FinanceResponse | null>(null);

  const load = useCallableAction<
    { preset: string; from?: string; to?: string },
    FinanceResponse
  >("getFinanceDashboard");

  const saveAd = useCallableAction<Record<string, unknown>, { id: string }>("saveAdSpend");
  const saveExpense = useCallableAction<Record<string, unknown>, { id: string }>(
    "saveFinanceExpense",
  );
  const removeEntry = useCallableAction<{ kind: string; id: string }, { ok: boolean }>(
    "deleteFinanceEntry",
  );

  /**
   * MUHASEBE FATURA RAPORU — iki sayfalı gerçek .xlsx.
   *
   * ★ NİÇİN SUNUCUDA ÜRETİLİYOR. Yanındaki "Excel'e indir" düğmesi ekrandaki
   * satırlardan CSV üretir; CSV tek sayfadır ve bu raporun İKİ sayfası var
   * (detay + muhasebe stoğuna göre birleştirilmiş fatura kalemleri). Tarayıcıda
   * çok sayfalı xlsx üretmek yeni bir bağımlılık ister, CONTRACT bunu yasaklıyor
   * (`lib/csv.ts` aynı gerekçeyi yazıyor). Sunucuda `exceljs` zaten izinli.
   *
   * ★ EKRANDAKİ ARALIK GÖNDERİLİR. Rapor "bu ay"a sabitlenseydi kullanıcı
   * ekranda Ağustos'a bakarken Eylül dosyası inerdi ve farkı ancak dosyayı
   * açınca görürdü.
   */
  const muhasebeRaporu = useCallableAction<
    { preset: string; from?: string; to?: string },
    {
      fileName: string;
      contentBase64?: string;
      url?: string;
      rowCount: number;
      groupCount: number;
      /** "İşbirliği" etiketli siparişler — ayrı sayfada, satış sayfalarında YOK. */
      isbirligiSiparis?: number;
      /** "Satış Detayı" sayfasının satır sayısı. `rowCount` işbirliği satırlarını da sayar. */
      satisSatir?: number;
      maliyetsizKalem: number;
      tanimsizKalem: number;
      truncated: boolean;
    }
  >("exportAccountingReport");
  const [muhasebeNot, setMuhasebeNot] = useState<string | null>(null);

  const indirMuhasebeRaporu = useCallback(async () => {
    setMuhasebeNot(null);
    try {
      const r = await muhasebeRaporu.run({
        preset: range.preset,
        from: range.from,
        to: range.to,
      });
      if (!r) return;
      // Dosya yanıtın içinde geldiyse `blob:` adresine çevrilir; sunucuya
      // gitmez, paylaşılamaz ve sekme kapanınca ölür (Reports.tsx ile aynı yol).
      if (r.contentBase64) {
        const bin = atob(r.contentBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = r.fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (r.url) {
        window.open(r.url, "_blank", "noopener");
      }
      /**
       * ★ EKSİKLER SESSİZ KALMAZ. Muhasebe stoğu tanımsız ya da maliyeti
       * hesaplanmamış kalem varsa fatura yanlış kesilir; kullanıcı bunu dosyayı
       * açmadan da görmeli.
       */
      const uyari: string[] = [];
      if (r.tanimsizKalem > 0) uyari.push(`${r.tanimsizKalem} kalemin muhasebe stoğu tanımsız`);
      if (r.maliyetsizKalem > 0) uyari.push(`${r.maliyetsizKalem} kalemin maliyeti hesaplanmamış`);
      if (r.truncated) uyari.push("liste sipariş sınırına dayandı, dönemi daraltın");
      // İşbirliği siparişleri satış sayfalarından ÇIKARILDI; kullanıcı onları
      // orada arayıp "kayboldu" sanmasın diye nerede olduklarını söyleriz.
      const isb = r.isbirligiSiparis ?? 0;
      const isbNotu = isb > 0 ? ` ${isb} işbirliği siparişi "İşbirliği" sayfasında ayrı.` : "";
      setMuhasebeNot(
        (uyari.length > 0
          ? `${r.fileName} indirildi — ${uyari.join(" · ")}.`
          : `${r.fileName} indirildi (${r.satisSatir ?? r.rowCount} satış satırı, ${r.groupCount} muhasebe stok grubu).`) +
          isbNotu,
      );
    } catch {
      // Hata metnini `useCallableAction` zaten taşıyor; ikinci kez yazmayalım.
    }
  }, [muhasebeRaporu, range.preset, range.from, range.to]);

  /**
   * ★ İSTEK SIRASI. Aralık değiştirici yükleme sırasında kilitlenmiyor (bilerek:
   * kullanıcıyı bekletmemek için) ve sunucu tarafı ağır — `getFinanceDashboard`
   * 300 sn zaman aşımıyla çalışıyor ve 3.000 siparişe kadar her sipariş için ayrı
   * bir alt koleksiyon okuması yapıyor. İki ön ayar hızlıca seçilirse ikinci istek
   * ÖNCE dönebilir; sırasız `setData` o zaman ekrana ESKİ aralığın rakamlarını
   * yazar ve başlıkta yeni aralık yazdığı için kullanıcı yanlış sayıyı doğru sanır.
   * Sayaç, yalnızca EN SON isteğin yazmasına izin verir.
   */
  const sonIstek = useRef(0);

  const refresh = useCallback(
    (v: RangeValue) => {
      const benim = ++sonIstek.current;
      load
        .run({ preset: v.preset, from: v.from, to: v.to })
        .then((res) => {
          if (res && benim === sonIstek.current) setData(res);
        })
        .catch(() => undefined);
    },
    [load],
  );

  useEffect(() => {
    refresh(range);
    try {
      // Yarım «Özel» aralık DEPOYA HİÇ YAZILMAZ (bkz. loadRange). Okuma tarafında
      // da eleniyor; iki katman, çünkü depoya bir kez yazılan bozuk kayıt
      // kullanıcının önüne her açılışta boş bir sayfa çıkarıyordu.
      if (range.preset !== "custom" || (range.from && range.to)) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
      }
    } catch {
      // Depolama kapalıysa ekran yine çalışır.
    }
    // `refresh` her render'da yeniden üretilmesin diye bağımlılık yalnızca aralıktır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.preset, range.from, range.to]);

  /* ── gider formu durumu ─────────────────────────────────────────────── */
  const [adOpen, setAdOpen] = useState(false);
  const [adForm, setAdForm] = useState({
    id: "",
    date: todayIso(),
    channel: "meta" as AdChannel,
    channelLabel: "",
    amount: "",
    note: "",
  });
  const [adError, setAdError] = useState<string | null>(null);

  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState({
    id: "",
    date: todayIso(),
    category: "operating" as ExpenseCategory,
    label: "",
    amount: "",
    note: "",
  });
  const [expError, setExpError] = useState<string | null>(null);

  const openNewAd = () => {
    setAdForm({ id: "", date: todayIso(), channel: "meta", channelLabel: "", amount: "", note: "" });
    setAdError(null);
    setAdOpen(true);
  };

  const openEditAd = (r: AdRow) => {
    setAdForm({
      id: r.id,
      date: r.dateKey || todayIso(),
      channel: r.channel,
      channelLabel: r.channel === "other" ? r.label : "",
      amount: formatKurusInput(r.amountKurus),
      note: r.note,
    });
    setAdError(null);
    setAdOpen(true);
  };

  const openNewExpense = (category: ExpenseCategory) => {
    setExpForm({ id: "", date: todayIso(), category, label: "", amount: "", note: "" });
    setExpError(null);
    setExpOpen(true);
  };

  const openEditExpense = (r: ExpenseRow) => {
    setExpForm({
      id: r.id,
      date: r.dateKey || todayIso(),
      category: r.category,
      label: r.label,
      amount: formatKurusInput(r.amountKurus),
      note: r.note,
    });
    setExpError(null);
    setExpOpen(true);
  };

  const submitAd = async () => {
    const kurus = parseKurus(adForm.amount);
    /**
     * ★ SIFIR GEÇERLİ, BOŞ DEĞİL. `parseKurus` bozuk girdide `null` döner —
     * `!kurus` yazılsaydı 0 TL'lik meşru bir kayıt da reddedilirdi.
     */
    if (kurus === null || kurus < 0) {
      setAdError("Tutarı sayı olarak yazın (örn. 1.250,50).");
      return;
    }
    if (adForm.channel === "other" && adForm.channelLabel.trim() === "") {
      setAdError("«Diğer» kanalda mecra adını yazın (TikTok, Pinterest…).");
      return;
    }
    setAdError(null);
    /**
     * ★ `id` ANAHTARI BOŞKEN HİÇ GÖNDERİLMEZ. Firebase callable SDK'sı gövdeyi
     * kodlarken `undefined` değerleri `null`'a çevirir; sunucudaki şema `null`
     * kabul etmediği için yeni kayıt açan form "Geçersiz girdi." ile düşüyordu.
     * Sunucu tarafı da (`zOptId`) artık `null`'a dayanıklı — iki katman birden.
     */
    const res = await saveAd.run({
      ...(adForm.id ? { id: adForm.id } : {}),
      date: adForm.date,
      channel: adForm.channel,
      channelLabel: adForm.channelLabel.trim(),
      amountKurus: kurus,
      note: adForm.note,
    });
    // ★ Sessiz başarı YOK: sunucu `null` döndüyse hata `saveAd.error`'dadır ve
    // pencere AÇIK kalır; kullanıcı kaydın gittiğini sanmasın.
    if (res) {
      setAdOpen(false);
      refresh(range);
    }
  };

  const submitExpense = async () => {
    const kurus = parseKurus(expForm.amount);
    if (kurus === null || kurus < 0) {
      setExpError("Tutarı sayı olarak yazın (örn. 12.000,00).");
      return;
    }
    if (expForm.label.trim().length < 2) {
      setExpError("Gider adını yazın (örn. Kira, SGK, Influencer).");
      return;
    }
    setExpError(null);
    const res = await saveExpense.run({
      ...(expForm.id ? { id: expForm.id } : {}),
      date: expForm.date,
      category: expForm.category,
      label: expForm.label.trim(),
      amountKurus: kurus,
      note: expForm.note,
    });
    if (res) {
      setExpOpen(false);
      refresh(range);
    }
  };

  const remove = async (kind: "adSpend" | "expense", id: string, label: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`"${label}" gider kaydı silinsin mi?`)) return;
    const res = await removeEntry.run({ kind, id });
    if (res) refresh(range);
  };

  /* ── tablo sütunları ────────────────────────────────────────────────── */

  /**
   * Satılan ürünler tablosunu Excel'in açtığı bir dosyaya indirir.
   *
   * ★ EKRANDAKİ VERİDEN üretilir, sunucuya ikinci bir sorgu gitmez — dosya ile
   * ekranın ayrışması yapısal olarak imkânsız olsun diye.
   *
   * ★ BİLİNMEYEN MALİYET BOŞ HÜCRE OLUR, SIFIR DEĞİL. Ekran bu satırlarda "—"
   * yazıyor; dosyada 0 yazmak, Excel'de toplanınca maliyeti olduğundan düşük
   * gösterirdi. Sebep ayrı bir sütunda okunur biçimde durur.
   *
   * ★ KIRPILMIŞ VERİ UYARISI dosyanın İÇİNE yazılır. Ekrandaki uyarıyı görüp
   * dosyayı başkasına gönderen kişi, karşı tarafın da görmesini sağlamalı;
   * yoksa eksik bir tablo tam sanılır.
   */
  const indirSatilanUrunler = useCallback(() => {
    // `sold` bu satirin ALTINDA tanimlaniyor; burada dogrudan `data`dan turetilir.
    const sold = data?.soldProducts;
    if (!data || !sold || sold.rows.length === 0) return;

    const basliklar = [
      "Ürün",
      "SKU",
      "Adet",
      "Birim Malzeme (TL)",
      "Toplam Malzeme (TL)",
      "Kargo (TL)",
      "Fatura Tutarı (TL)",
      "Maliyet Durumu",
    ];

    const satirlar = sold.rows.map((r) => [
      r.title,
      r.sku,
      csvSayi(r.quantity),
      csvTutar(r.unitMaterialKurus),
      csvTutar(r.totalMaterialKurus),
      csvTutar(r.totalCargoKurus),
      csvTutar(r.invoiceKurus),
      r.reason ? UNCOSTED_REASON_LABELS[r.reason] : "maliyeti bilinen",
    ]);

    // Toplam satırı: Excel'de elle toplamaya gerek kalmasın.
    satirlar.push([]);
    satirlar.push([
      "TOPLAM",
      "",
      "",
      "",
      csvTutar(sold.materialKurus),
      csvTutar(sold.cargoKurus),
      csvTutar(sold.invoiceKurus),
      `${sold.costedItems} kalem maliyeti bilinen · ${sold.uncostedItems} bilinmeyen`,
    ]);

    satirlar.push([]);
    satirlar.push([`Dönem: ${data.rangeLabel}`]);
    satirlar.push([`Fatura çarpanı: malzeme × ${data.settings.invoiceMultiplier}`]);
    if (sold.estimatedCargoQuantity > 0) {
      satirlar.push([
        `UYARI: ${sold.estimatedCargoQuantity} kalemin kargosu ölçülmedi, ` +
          `satış fiyatının %${sold.cargoEstimatePercent}'i olarak tahmin edildi.`,
      ]);
    }
    if (data.revenue.truncated) {
      satirlar.push([
        `UYARI: Sipariş listesi ${n(data.revenue.limit)} kayıtla sınırlı; ` +
          "bu tablo dönemin TAMAMINI kapsamıyor.",
      ]);
    }

    csvIndir(
      `satilan-urunler-${dosyaParcasi(data.rangeLabel)}.csv`,
      csvMetni(basliklar, satirlar),
    );
  }, [data]);

  const soldColumns: Array<Column<SoldProductRow>> = useMemo(
    () => [
      {
        key: "title",
        header: "Ürün",
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">{r.title}</p>
            <p className="text-xs text-gray-500">
              {r.sku ? `SKU ${r.sku}` : "SKU yok"}
              {/* ★ Maliyeti bilinmeyen satır SEBEBİYLE birlikte yazılır. */}
              {r.reason ? ` · ${UNCOSTED_REASON_LABELS[r.reason]}` : ""}
            </p>
          </div>
        ),
      },
      { key: "qty", header: "Adet", numeric: true, render: (r) => n(r.quantity, "") },
      {
        key: "unit",
        header: "Birim Malzeme",
        numeric: true,
        hideOnMobile: true,
        // `tl()` null'da "—" yazar; SIFIR YAZMAZ. İkisi ayrı anlam taşır.
        render: (r) => tl(r.unitMaterialKurus),
      },
      {
        key: "total",
        header: "Toplam Malzeme",
        numeric: true,
        render: (r) => tl(r.totalMaterialKurus),
      },
      {
        key: "invoice",
        header: "Fatura Tutarı",
        numeric: true,
        render: (r) => (
          <span className={r.invoiceKurus === null ? "" : "font-semibold text-gray-900"}>
            {tl(r.invoiceKurus)}
          </span>
        ),
      },
    ],
    [],
  );

  const adColumns: Array<Column<AdRow>> = useMemo(
    () => [
      { key: "date", header: "Tarih", render: (r) => r.dateKey || "—" },
      {
        key: "channel",
        header: "Kanal",
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate text-gray-900">{r.label}</p>
            {r.note ? <p className="truncate text-xs text-gray-500">{r.note}</p> : null}
          </div>
        ),
      },
      {
        key: "source",
        header: "Kaynak",
        hideOnMobile: true,
        render: (r) => (r.source === "manual" ? "Elle girildi" : r.source),
      },
      { key: "amount", header: "Tutar", numeric: true, render: (r) => tl(r.amountKurus) },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <div className="flex justify-end gap-2">
            <button type="button" className="link text-sm" onClick={() => openEditAd(r)}>
              Düzenle
            </button>
            <button
              type="button"
              className="text-sm text-red-700 hover:underline"
              onClick={() => remove("adSpend", r.id, r.label)}
            >
              Sil
            </button>
          </div>
        ),
      },
    ],
    // Satır eylemleri kapanışları her render'da yenilenir; tablo küçük, maliyeti yok.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range],
  );

  const expenseColumns: Array<Column<ExpenseRow>> = useMemo(
    () => [
      { key: "date", header: "Tarih", render: (r) => r.dateKey || "—" },
      {
        key: "label",
        header: "Kalem",
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate text-gray-900">{r.label}</p>
            {r.note ? <p className="truncate text-xs text-gray-500">{r.note}</p> : null}
          </div>
        ),
      },
      {
        key: "category",
        header: "Tür",
        hideOnMobile: true,
        render: (r) =>
          EXPENSE_CATEGORY_OPTIONS.find((c) => c.value === r.category)?.label ?? r.category,
      },
      { key: "amount", header: "Tutar", numeric: true, render: (r) => tl(r.amountKurus) },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <div className="flex justify-end gap-2">
            <button type="button" className="link text-sm" onClick={() => openEditExpense(r)}>
              Düzenle
            </button>
            <button
              type="button"
              className="text-sm text-red-700 hover:underline"
              onClick={() => remove("expense", r.id, r.label)}
            >
              Sil
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range],
  );

  const sshColumns: Array<Column<SshCostItem>> = useMemo(
    () => [
      { key: "date", header: "Açılış", render: (r) => r.dateKey || "—" },
      {
        key: "ssh",
        header: "SSH kaydı",
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate text-gray-900">
              {r.productTitle || "(ürün belirtilmemiş)"}
              {r.quantity > 1 ? ` ×${r.quantity}` : ""}
            </p>
            <p className="truncate text-xs text-gray-500">
              {r.orderName ? `${r.orderName} · ` : ""}
              {r.problemNote || "açıklama yok"}
            </p>
          </div>
        ),
      },
      {
        key: "status",
        header: "Durum",
        hideOnMobile: true,
        render: (r) => r.statusLabel || r.status || "—",
      },
      {
        key: "cost",
        header: "Gider Tutarı",
        numeric: true,
        render: (r) =>
          /* ★ Girilmemiş tutar "0,00 ₺" YAZILMAZ; ikisi ayrı anlam taşır. */
          r.costKurus === null ? (
            <span className="text-xs font-medium italic text-amber-800">girilmemiş</span>
          ) : (
            <span className="font-semibold text-gray-900">{tl(r.costKurus)}</span>
          ),
      },
    ],
    [],
  );

  const busy = load.loading;
  const s = data?.summary;
  const sold = data?.soldProducts;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Finans"
        description={
          <>
            Seçilen dönemin cirosu, satılan ürünlerin malzeme maliyeti, kesilecek fatura
            tutarı, giderler ve tahmini kârlılık. Bu ekranı yalnızca iş sahibi görebilir.
          </>
        }
        actions={
          <DateRangePicker
            value={range}
            onChange={setRange}
            resolvedLabel={data?.rangeLabel ?? null}
            busy={busy}
          />
        }
      />

      {load.error ? <ErrorNote error={load.error} /> : null}
      {saveAd.error ? <ErrorNote error={saveAd.error} /> : null}
      {saveExpense.error ? <ErrorNote error={saveExpense.error} /> : null}
      {removeEntry.error ? <ErrorNote error={removeEntry.error} /> : null}

      {/* Fatura çarpanı ve SSH gideri — ayarını rakamın yanında tut. */}
      <FinanceParams onSaved={() => refresh(range)} />

      {busy && !data ? <LoadingBlock /> : null}

      {data ? (
        <>
          {/* ── 1. ÜST KARTLAR ─────────────────────────────────────────── */}
          <section aria-labelledby="ozet-baslik">
            <h2
              id="ozet-baslik"
              className="section-heading"
            >
              {data.rangeLabel} · {data.days} gün
            </h2>
            <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Toplam Ciro"
                value={data.revenue.measurable ? tl(data.revenue.kurus) : "ölçülemedi"}
                tone={data.revenue.measurable ? "normal" : "muted"}
                basis={
                  data.revenue.measurable
                    ? `${n(data.revenue.paidOrderCount ?? data.revenue.orderCount, " ödenmiş sipariş")} · ${data.revenue.basis}`
                    : "ciroya sayılan statü tanımlı değil"
                }
              />
              <StatCard
                label="Toplam Malzeme Maliyeti"
                value={tl(sold?.materialKurus)}
                basis={
                  `${n(sold?.costedItems, " kalem")} · maliyeti bilinen kalemler` +
                  ((data.collab?.orderCount ?? 0) > 0 ? " · işbirliği hariç" : "")
                }
              />
              <StatCard
                label="Kesilecek Fatura"
                value={tl(sold?.invoiceKurus)}
                basis={
                  // Excel raporundaki tutar da KDV HARİÇtir (2026-10-09) — aynı rakam.
                  `toplam malzeme maliyeti × ${data.settings.invoiceMultiplier} · KDV hariç` +
                  ((data.collab?.orderCount ?? 0) > 0 ? " · işbirliği hariç" : "")
                }
              />
              <StatCard
                label="Tahmini Net Kâr"
                value={tl(s?.netProfitKurus)}
                tone={s?.netProfitKurus === undefined ? undefined : s.netProfitKurus >= 0 ? "good" : "bad"}
                basis={
                  s?.marginPercent === null || s?.marginPercent === undefined
                    ? "ciro yok — marj hesaplanamadı"
                    : `kâr marjı ${pct(s.marginPercent)}`
                }
              />
            </dl>
          </section>

          {/* ── 6. KÂRLILIK ÖZETİ ──────────────────────────────────────── */}
          <Collapsible
            storageKey="finans.karlilik"
            defaultOpen
            title={
              <span className="inline-flex items-center gap-2">
                Kârlılık Özeti
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                  Gizli
                </span>
              </span>
            }
            meta={`toplam gider ${tl(s?.totalExpenseKurus)} · tahmini net kâr ${tl(s?.netProfitKurus)}${
              s?.marginPercent === null || s?.marginPercent === undefined ? "" : ` · marj ${pct(s.marginPercent)}`
            }`}
          >

            {/*
              ★ EKSİK TOPLAM UYARISI — bu ekranın en pahalı sessiz hatası burada.
              Ciro 3.000 siparişle, reklam/gider/SSH ise 1.000 satırla sınırlı.
              Kırpma OLUŞTUĞUNDA yön tek taraflıdır: eksik kalan GİDER, net kârı
              olduğundan YÜKSEK gösterir. Rakamı gizlemek yerine güvenilmez
              olduğunu söylüyoruz — yanlış rakama güvenmek, rakamsız kalmaktan
              pahalıdır (bu dosyanın başlığındaki söz).
            */}
            {(() => {
              const eksik = [
                data.revenue.truncated ? `ciro ve malzeme (en yeni ${n(data.revenue.limit)} sipariş)` : "",
                data.ads.truncated ? `reklam gideri (${n(data.ads.limit)} satır)` : "",
                data.expenses.truncated ? `diğer giderler (${n(data.expenses.limit)} satır)` : "",
                data.ssh.truncated ? `SSH gideri (${n(data.ssh.limit)} kayıt)` : "",
              ].filter(Boolean);
              if (eksik.length === 0) return null;
              return (
                <p
                  role="status"
                  className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
                >
                  <strong>Bu özet eksik veriyle hesaplandı.</strong> Şu kalemler sınıra
                  dayandı ve fazlası hesaba girmedi: {eksik.join(" · ")}. Kırpılan
                  giderler toplama girmediği için <strong>net kâr olduğundan yüksek
                  görünür</strong>. Daha dar bir tarih aralığı seçin.
                </p>
              );
            })()}

            <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
              <SummaryLine label="Toplam Ciro" value={tl(s?.revenueKurus)} />
              <SummaryLine
                label="Toplam Malzeme Maliyeti"
                value={`− ${tl(s?.materialKurus)}`}
                hint={
                  sold && sold.uncostedItems > 0
                    ? `${sold.uncostedItems} kalemin maliyeti bilinmiyor — bu tutara DÂHİL DEĞİL`
                    : undefined
                }
              />
              {/*
                ★ KARGO AYRI SATIR — ÇİFT SAYIM YOK.
                Ürün maliyeti `costing/compute.ts` içinde malzeme ve kargo olarak
                AYRI hesaplanır (`totalCostKurus = materialKurus + cargoKurus`).
                Canlı ölçüm: 516/516 üründe bu eşitlik sapmasız tutuyor ve 312
                bileşenin hiçbirinin adında kargo/nakliye geçmiyor — yani kargo
                malzemenin içine elle de girilmemiş. İki satırın toplamı ürün
                kartındaki toplam maliyetle birebir aynı.
              */}
              <SummaryLine
                label="Kargo Maliyeti"
                value={`− ${tl(s?.cargoKurus)}`}
                hint={
                  sold && sold.estimatedCargoQuantity > 0
                    ? `${sold.estimatedCargoQuantity} adedi ÖLÇÜM DEĞİL, satış fiyatının %${sold.cargoEstimatePercent}'i olarak TAHMİN edildi`
                    : "ürün kartlarındaki desi ölçümünden"
                }
              />
              <SummaryLine
                label={`KDV (%${data.settings.kdv})`}
                value={`− ${tl(s?.kdvKurus)}`}
                hint="devlete ait — kâr değildir"
              />
              {/* ★ KANAL BAZLI KESİNTİ. Trendyol tahsilatı kendisi yapar ve %16,1
                  net komisyon keser; banka/ödeme ve Shopify kesintisi o ciroya
                  uygulanmaz. Eskiden tüm ciroya %10 uygulanıyordu. */}
              <SummaryLine
                label={
                  (s?.trendyolRevenueKurus ?? 0) > 0
                    ? `Komisyon (Shopify/diğer %${data.settings.komisyon + data.settings.shopifyKomisyon} · Trendyol %${String(data.settings.trendyolKomisyon ?? "").replace(".", ",")})`
                    : `Komisyon (banka/ödeme %${data.settings.komisyon} + Shopify %${data.settings.shopifyKomisyon})`
                }
                value={`− ${tl(s?.commissionKurus)}`}
                hint={
                  (s?.trendyolRevenueKurus ?? 0) > 0
                    ? `Trendyol: ${tl(s?.trendyolRevenueKurus)} ciro × %${String(data.settings.trendyolKomisyon ?? "").replace(".", ",")} = ${tl(s?.trendyolCommissionKurus)} · kalan ciro × %${data.settings.komisyon + data.settings.shopifyKomisyon} = ${tl((s?.commissionKurus ?? 0) - (s?.trendyolCommissionKurus ?? 0))}`
                    : "ciro üzerinden kesinti"
                }
              />
              <SummaryLine label="Reklam Giderleri" value={`− ${tl(s?.adKurus)}`} />
              <SummaryLine label="Personel Giderleri" value={`− ${tl(s?.personnelKurus)}`} />
              <SummaryLine
                label="Genel İşletme Giderleri"
                value={`− ${tl(s?.operatingKurus)}`}
              />
              <SummaryLine
                label="SSH Giderleri"
                value={`− ${tl(s?.sshKurus)}`}
                hint={data.ssh.basis}
              />
              <SummaryLine label="Özel (Custom) Giderler" value={`− ${tl(s?.customKurus)}`} />
              {/* ★ İŞBİRLİĞİ AYRI GİDER SATIRI. Bu ürünlerin cirosu 0 ₺'dir;
                  maliyetleri satış maliyetine karışmaz ama gerçek bir giderdir. */}
              {(data.collab?.orderCount ?? 0) > 0 ? (
                <SummaryLine
                  label="İşbirliği Gideri"
                  value={`− ${tl(s?.isbirligiKurus ?? 0)}`}
                  hint={
                    `${n(data.collab?.orderCount)} işbirliği siparişinin ürün maliyeti + kargosu — satış maliyetinden ayrı` +
                    // ★ Sessiz sıfır yok: eksik maliyet ve tahmini kargo bu satırda da yazar.
                    ((data.collab?.uncostedItems ?? 0) > 0
                      ? ` · ${n(data.collab?.uncostedItems)} kalemin maliyeti bilinmiyor — bu tutara DÂHİL DEĞİL`
                      : "") +
                    ((data.collab?.estimatedCargoQuantity ?? 0) > 0
                      ? ` · ${n(data.collab?.estimatedCargoQuantity)} adedin kargosu TAHMİN`
                      : "")
                  }
                />
              ) : null}
              <SummaryLine label="Toplam Gider" value={tl(s?.totalExpenseKurus)} strong />
              <SummaryLine
                label="Tahmini Net Kâr"
                value={tl(s?.netProfitKurus)}
                strong
                tone={s?.netProfitKurus === undefined ? undefined : s.netProfitKurus >= 0 ? "good" : "bad"}
              />
              <SummaryLine
                label="Kâr Marjı"
                value={
                  s?.marginPercent === null || s?.marginPercent === undefined
                    ? "—"
                    : pct(s.marginPercent)
                }
                hint="net kâr / toplam ciro"
                strong
                /* ★ `?? 0` KELEPÇESİ KALDIRILDI. Marj yalnızca ciro > 0 iken
                   hesaplanır (finance/model.ts), aksi hâlde `null` döner ve değer
                   "—" basılır. Eski kelepçe null'ı 0'a çevirip `0 >= 0` ile YEŞİL
                   yapıyordu: satışsız bir dönemde ekran "—" yazıp aynı anda
                   "sağlıklı" rengi gösteriyordu. Değer yoksa renk de olmamalı. */
                tone={
                  s?.marginPercent === null || s?.marginPercent === undefined
                    ? undefined
                    : s.marginPercent >= 0
                      ? "good"
                      : "bad"
                }
              />
            </div>

            {/*
              ★ "HESABA DÂHİL OLMAYANLAR" BLOĞU KALDIRILDI.
              KDV, komisyon ve kargo artık ana toplamın İÇİNDE (iş sahibi kararı,
              2026-08-21). Aynı üç rakamı biri toplanan biri toplanmayan iki ayrı
              yerde tutmak "hangisi doğru?" sorusunu doğururdu.
            */}
            <p className="mt-3 text-xs leading-relaxed text-gray-600">
              Bu hesap cironun tamamından TÜM giderleri düşer. KDV arındırılmış net
              hasılat ayrıca {tl(data.netRevenueKurus)}; ürün kartındaki kâr oranı ise
              farklı bir tabana bakar ({data.basisLabel}) — iki rakamı karşılaştırırken
              tabanın aynı olmadığını unutmayın.
            </p>
          </Collapsible>

          {/* ── 1b. CİRO DÖKÜMÜ ────────────────────────────────────────── */}
          {data.revenue.measurable && data.revenue.breakdown ? (
            <RevenueBreakdownSection
              b={data.revenue.breakdown}
              kdv={data.settings.kdv}
              totalKurus={data.revenue.kurus}
              // Ana ciro sorgusu da kesildiyse döküm onunla karşılaştırılamaz.
              truncated={Boolean(data.revenue.breakdownTruncated || data.revenue.truncated)}
              limit={data.revenue.breakdownLimit ?? 0}
              mismatch={Boolean(data.revenue.breakdownMismatch)}
            />
          ) : null}

          {/* ── 1c. İŞBİRLİĞİ — normal cirodan ve satış maliyetinden AYRI ── */}
          {data.collab && data.collab.orderCount > 0 ? (
            <Collapsible
              storageKey="finans.isbirligi"
              title="İşbirliği siparişleri — normal satıştan ayrı"
              meta={`${n(data.collab.orderCount, " sipariş")} · ürün maliyeti ${tl(data.collab.materialKurus)} · kesilecek fatura ${tl(data.collab.invoiceKurus)}`}
            >
              <p className="text-xs text-gray-500">
                Shopify'da "İşbirliği" etiketli siparişler. Satış değildir: ciroya, satılan ürün
                maliyetine ve Kesilecek Fatura'ya girmez; maliyeti Kârlılık Özeti'nde ayrı bir gider satırıdır.
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-4 text-sm lg:grid-cols-5">
                <div>
                  <dt className="text-xs text-gray-500">Sipariş</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{n(data.collab.orderCount)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Liste değeri</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{tl(data.collab.listKurus)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Ürün maliyeti</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{tl(data.collab.materialKurus)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Kargo maliyeti</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{tl(data.collab.cargoKurus)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">
                    Kesilecek fatura (× {data.settings.invoiceMultiplier}, KDV hariç)
                  </dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{tl(data.collab.invoiceKurus)}</dd>
                </div>
              </dl>
              {data.collab.uncostedItems > 0 ? (
                <p className="mt-2 text-xs text-red-800">
                  {n(data.collab.uncostedItems)} işbirliği kaleminin maliyeti bilinmiyor — bu tutarlara
                  DÂHİL DEĞİL.
                </p>
              ) : null}
              {data.collab.netKurus > 0 ? (
                <p className="mt-2 text-xs text-amber-800">
                  Bu siparişlerde {tl(data.collab.netKurus)} tahsil edilmiş; Toplam Ciro'ya dâhildir.
                </p>
              ) : null}
            </Collapsible>
          ) : null}

          {/* ── 2. VERİ GÜVENİLİRLİĞİ UYARILARI ────────────────────────── */}
          {sold && sold.uncostedItems > 0 ? (
            <div
              role="alert"
              className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
            >
              <strong>
                {sold.uncostedItems} kalemin malzeme maliyeti bilinmiyor
                {sold.uncostedQuantity > 0 ? ` (${sold.uncostedQuantity} adet)` : ""}.
              </strong>{" "}
              Bu kalemler toplamlara <strong>hiç girmedi</strong> — sıfır maliyet olarak
              sayılsalardı kâr olduğundan yüksek görünürdü. Sebepler:{" "}
              {(Object.keys(sold.reasons) as UncostedReason[])
                .filter((k) => sold.reasons[k] > 0)
                .map((k) => `${sold.reasons[k]}× ${UNCOSTED_REASON_LABELS[k]}`)
                .join(" · ")}
              . Tablonun en üstündeki satırlar bu ürünlerdir; reçeteleri
              tamamlandığında rakamlar kendiliğinden düzelir.
            </div>
          ) : null}

          {sold && sold.estimatedCargoQuantity > 0 ? (
            <p
              role="status"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              <strong>{sold.estimatedCargoQuantity} adet</strong> ürünün kargo maliyeti
              ölçülmedi, satış fiyatının %{sold.cargoEstimatePercent}
              &apos;i olarak <strong>tahmin edildi</strong> (koli ölçüsü girilmemiş). Bu
              tahmin <strong>Kargo Maliyeti satırına ve Toplam Gider&apos;e dâhildir</strong>,
              yani net kârı doğrudan etkiler. Kesin rakam için ürün kartına koli ölçüsü
              (desi) girilmelidir.
            </p>
          ) : null}

          {/* ── 3. SATILAN ÜRÜNLER + FATURA ────────────────────────────── */}
          {/* Rapor düğmesi bölüm başlığında; sonucu bölüm KAPALIYKEN de görünsün. */}
          {muhasebeRaporu.error ? (
            <p role="alert" className="text-sm text-red-700">
              Muhasebe raporu üretilemedi: {muhasebeRaporu.error}
            </p>
          ) : null}
          {muhasebeNot ? <p className="text-sm text-gray-700">{muhasebeNot}</p> : null}
          <Collapsible
            storageKey="finans.urunler"
            title="Satılan Ürünler ve Fatura Hesabı"
            meta={
              sold
                ? `${n(sold.rows.length, " ürün satırı")} · malzeme ${tl(sold.materialKurus)} · kesilecek fatura ${tl(sold.invoiceKurus)}`
                : undefined
            }
            actions={
              sold && sold.rows.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => indirSatilanUrunler()}
                    className="btn-secondary"
                  >
                    Excel&apos;e indir ({n(sold.rows.length, " satır")})
                  </button>
                  <button
                    type="button"
                    onClick={() => void indirMuhasebeRaporu()}
                    disabled={muhasebeRaporu.loading}
                    className="btn-primary"
                    title="Sipariş kırılımı + muhasebe stoğuna göre birleştirilmiş fatura kalemleri + işbirliği siparişleri ayrı sayfada"
                  >
                    {muhasebeRaporu.loading ? "Hazırlanıyor…" : "Muhasebe fatura raporu"}
                  </button>
                </>
              ) : null
            }
          >
            <DataTable
              caption={`${data.rangeLabel} aralığında satılan ürünler`}
              rows={sold?.rows ?? []}
              initialRows={15}
              columns={soldColumns}
              /**
               * ★ MALİYET DURUMU ANAHTARA GİRER. `aggregateSoldProducts` aynı
               * ürünü İKİ AYRI kovaya koyabiliyor: maliyeti bilinen kalemler bir
               * satır, maliyeti bilinmeyenler (`reason` dolu) ayrı bir satır.
               * Anahtar yalnızca `productId` olduğunda iki satır AYNI React
               * anahtarını taşıyor; React satırları karıştırıyor ve tabloda
               * yanlış rakam yanlış ürünün yanında görünebiliyor.
               */
              rowKey={(r) => `${r.productId || `baslik:${r.title}`}${r.reason ? `:${r.reason}` : ""}`}
              emptyTitle="Bu aralıkta satılan ürün yok"
              emptyDescription="Tarih aralığını genişletmeyi deneyin."
              footer={
                sold && sold.rows.length > 0 ? (
                  <div className="flex flex-wrap items-baseline justify-between gap-3 px-4 py-3 text-sm">
                    <span className="text-gray-600">
                      Toplam malzeme maliyeti {tl(sold.materialKurus)} · çarpan{" "}
                      {data.settings.invoiceMultiplier}
                    </span>
                    <span className="text-base font-semibold tabular-nums text-gray-900">
                      Toplam kesilecek fatura: {tl(sold.invoiceKurus)}
                    </span>
                  </div>
                ) : null
              }
            />
            {/*
              ★ SESSİZ KESME YASAK — liste sınıra dayandıysa ekran SÖYLER.

              `shown` olarak ham `orderCount` verilemez: `ListLimitNotice`
              `shown < limit` iken hiçbir şey yazmaz ve silinmiş siparişler
              elendiği için işlenen sayı sınırın bir altına düşebilir. O
              durumda tam 3.000 siparişte kesilmiş bir rakam UYARISIZ kalırdı
              — kırpıldığını bilmemek, kırpılmış rakamı görmekten pahalıdır.
              Otorite sunucunun `truncated` bayrağıdır.

              Canlı ölçüm (2026-08-21): «Maksimum» ön ayarında aralıkta 14.017
              sipariş var, 3.000'i işleniyor → uyarı burada çıkar. Aylık
              aralıklarda (52–75 sipariş) hiç çıkmaz.
            */}
            <ListLimitNotice
              shown={data.revenue.truncated ? data.revenue.limit : data.revenue.orderCount}
              total={data.revenue.matchedOrderCount}
              limit={data.revenue.limit}
              hint="Görünmeyenler EN ESKİ siparişlerdir. Daha dar bir tarih aralığı seçerek tam rakamı görebilirsiniz."
            />
          </Collapsible>

          {/* ── 4. REKLAM GİDERLERİ ────────────────────────────────────── */}
          <Collapsible
            storageKey="finans.reklam"
            title="Reklam Giderleri"
            meta={`toplam ${tl(data.ads.total)} · ${n(data.ads.rows.length, " kayıt")}`}
            actions={
              <button type="button" className="btn-secondary" onClick={openNewAd}>
                Reklam gideri ekle
              </button>
            }
          >

            <dl className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.ads.byChannel.map((c) => (
                <StatCard
                  key={c.channel}
                  label={c.label}
                  value={tl(c.amountKurus)}
                  basis={`${n(c.count, " kayıt")}`}
                />
              ))}
              <StatCard
                label="Toplam Reklam Gideri"
                value={tl(data.ads.total)}
                basis="Meta + Google + Diğer"
              />
            </dl>

            {data.ads.rows.length === 0 ? (
              <EmptyState
                title={
                  data.ads.otherCount > 0
                    ? `${data.presetLabel} aralığında reklam gideri yok`
                    : "Reklam gideri girilmemiş"
                }
                description={outsideHint(data.ads, "reklam gideri") ??
                  "Meta ve Google harcamaları elle girilir; API entegrasyonu henüz yok."}
              />
            ) : (
              <DataTable
                caption="Reklam gideri kayıtları"
                rows={data.ads.rows}
                columns={adColumns}
                rowKey={(r) => r.id}
              />
            )}
          </Collapsible>

          {/* ── 5. DİĞER GİDERLER ──────────────────────────────────────── */}
          <Collapsible
            storageKey="finans.giderler"
            title="Personel · Genel İşletme · Özel Giderler"
            meta={
              data.expenses.byCategory.map((c) => `${c.label.replace(" Giderleri", "")} ${tl(c.amountKurus)}`).join(" · ")
            }
            actions={EXPENSE_CATEGORY_OPTIONS.map((c) => (
              <button
                key={c.value}
                type="button"
                className="btn-secondary"
                onClick={() => openNewExpense(c.value)}
              >
                {c.label.replace(" Giderleri", "")} ekle
              </button>
            ))}
          >

            <dl className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {data.expenses.byCategory.map((c) => (
                <StatCard
                  key={c.category}
                  label={c.label}
                  value={tl(c.amountKurus)}
                  basis={`${n(c.count, " kayıt")}`}
                />
              ))}
            </dl>

            {data.expenses.rows.length === 0 ? (
              <EmptyState
                title={
                  data.expenses.otherCount > 0
                    ? `${data.presetLabel} aralığında gider kaydı yok`
                    : "Gider kaydı girilmemiş"
                }
                description={outsideHint(data.expenses, "gider kaydı") ??
                  "Maaş, SGK, kira, elektrik, muhasebe ve tek seferlik giderler buraya girilir."}
              />
            ) : (
              <DataTable
                caption="Gider kayıtları"
                rows={data.expenses.rows}
                columns={expenseColumns}
                rowKey={(r) => r.id}
              />
            )}
          </Collapsible>

          {/* ── 5b. SSH GİDERLERİ — GERÇEK KAYITLARDAN ─────────────────── */}
          <Collapsible
            storageKey="finans.ssh"
            title="SSH (Satış Sonrası Hizmet) Giderleri"
            meta={`toplam ${tl(data.ssh.kurus)} · ${n(data.ssh.recordCount, " kayıt")}${
              data.ssh.uncostedCount > 0 ? ` · ${n(data.ssh.uncostedCount)} kaydın tutarı girilmemiş` : ""
            }`}
            actions={
              <Link to="/after-sales" className="link text-sm">
                SSH ekranına git
              </Link>
            }
          >

            <dl className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard
                label="Toplam SSH Gideri"
                value={tl(data.ssh.kurus)}
                basis={data.ssh.basis}
              />
              <StatCard
                label="Dönemdeki SSH Kaydı"
                value={n(data.ssh.recordCount, " kayıt")}
                basis={`${data.ssh.dateFieldLabel} (${data.ssh.dateField}) esas alınır`}
              />
              <StatCard
                label="Tutarı Girilmemiş"
                value={
                  data.ssh.uncostedCount === 0
                    ? "yok"
                    : n(data.ssh.uncostedCount, " kayıt")
                }
                tone={data.ssh.uncostedCount === 0 ? "good" : "muted"}
                basis={
                  data.ssh.uncostedCount === 0
                    ? "tüm kayıtların tutarı girilmiş"
                    : "bu kayıtlar toplama DÂHİL DEĞİL"
                }
              />
            </dl>

            {/*
              ★ TUTARI BOŞ KAYIT SESSİZCE 0 SAYILMAZ.
              Sıfır sayılsaydı SSH gideri olduğundan DÜŞÜK, kâr olduğundan
              YÜKSEK görünürdü. Projedeki "sessiz kesme yasak" kuralının aynısı.
            */}
            {data.ssh.uncostedCount > 0 ? (
              <div
                role="alert"
                className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              >
                <strong>
                  {data.ssh.uncostedCount} SSH kaydının tutarı girilmemiş.
                </strong>{" "}
                Bu kayıtlar SSH gideri toplamına <strong>hiç girmedi</strong> — 0 TL
                sayılsalardı SSH gideri olduğundan düşük, kâr olduğundan yüksek
                görünürdü. Tutarlar <strong>SSH ekranından</strong> kayıt bazında
                girilir; girildikçe bu rakam kendiliğinden düzelir.
              </div>
            ) : null}

            {data.ssh.items.length === 0 ? (
              <EmptyState
                title="Bu aralıkta SSH kaydı yok"
                description="SSH gideri, kaydın AÇILDIĞI tarihe göre bu döneme yazılır."
              />
            ) : (
              <DataTable
                caption="Dönemdeki SSH kayıtları ve gider tutarları"
                rows={data.ssh.items}
                columns={sshColumns}
                rowKey={(r) => r.sshId}
              />
            )}
          </Collapsible>


        </>
      ) : null}

      {/* ══════════════════════════════════════════════════════════════════
       * REKLAM GİDERİ FORMU
       * ═══════════════════════════════════════════════════════════════ */}
      <Modal
        open={adOpen}
        title={adForm.id ? "Reklam giderini düzenle" : "Reklam gideri ekle"}
        description="Meta ve Google harcamaları elle girilir. API entegrasyonu henüz yok; kayıtlar sonradan otomatik beslemeye hazır tutuluyor."
        onClose={() => setAdOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setAdOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={saveAd.loading}
              onClick={submitAd}
            >
              {saveAd.loading ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label="Tarih" required htmlFor="ad-tarih">
            <input
              id="ad-tarih"
              type="date"
              className="input w-full"
              value={adForm.date}
              onChange={(e) => setAdForm({ ...adForm, date: e.target.value })}
            />
          </Field>

          <Field label="Kanal" required htmlFor="ad-kanal">
            <select
              id="ad-kanal"
              className="input w-full"
              value={adForm.channel}
              onChange={(e) =>
                setAdForm({ ...adForm, channel: e.target.value as AdChannel })
              }
            >
              {AD_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          {adForm.channel === "other" ? (
            <Field
              label="Mecra adı"
              required
              hint="Adsız «Diğer» kayıtları birikince o paranın nereye gittiği bir daha öğrenilemez."
              htmlFor="ad-mecra"
            >
              <input
                id="ad-mecra"
                type="text"
                className="input w-full"
                value={adForm.channelLabel}
                onChange={(e) => setAdForm({ ...adForm, channelLabel: e.target.value })}
                placeholder="TikTok, Pinterest, gazete ilanı…"
              />
            </Field>
          ) : null}

          <Field label="Tutar" required htmlFor="ad-tutar">
            <MoneyInput
              id="ad-tutar"
              value={adForm.amount}
              onChange={(v) => setAdForm({ ...adForm, amount: v })}
            />
          </Field>

          <Field label="Not" htmlFor="ad-not">
            <input
              id="ad-not"
              type="text"
              className="input w-full"
              value={adForm.note}
              onChange={(e) => setAdForm({ ...adForm, note: e.target.value })}
              placeholder="Kampanya adı, dönem bilgisi…"
            />
          </Field>

          {adError ? <ErrorNote error={adError} /> : null}
          {saveAd.error ? <ErrorNote error={saveAd.error} /> : null}
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════
       * GİDER FORMU (personel / genel / özel)
       * ═══════════════════════════════════════════════════════════════ */}
      <Modal
        open={expOpen}
        title={expForm.id ? "Gideri düzenle" : "Gider ekle"}
        onClose={() => setExpOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setExpOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={saveExpense.loading}
              onClick={submitExpense}
            >
              {saveExpense.loading ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label="Tarih" required htmlFor="gider-tarih">
            <input
              id="gider-tarih"
              type="date"
              className="input w-full"
              value={expForm.date}
              onChange={(e) => setExpForm({ ...expForm, date: e.target.value })}
            />
          </Field>

          <Field label="Gider türü" required htmlFor="gider-tur">
            <select
              id="gider-tur"
              className="input w-full"
              value={expForm.category}
              onChange={(e) =>
                setExpForm({ ...expForm, category: e.target.value as ExpenseCategory })
              }
            >
              {EXPENSE_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Kalem adı"
            required
            hint="Hazır adlardan seçebilir ya da kendiniz yazabilirsiniz."
            htmlFor="gider-ad"
          >
            <>
              <input
                id="gider-ad"
                type="text"
                className="input w-full"
                list="gider-oneri"
                value={expForm.label}
                onChange={(e) => setExpForm({ ...expForm, label: e.target.value })}
                placeholder="Kira, SGK, Influencer…"
              />
              <datalist id="gider-oneri">
                {EXPENSE_SUGGESTIONS[expForm.category].map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </>
          </Field>

          <Field label="Tutar" required htmlFor="gider-tutar">
            <MoneyInput
              id="gider-tutar"
              value={expForm.amount}
              onChange={(v) => setExpForm({ ...expForm, amount: v })}
            />
          </Field>

          <Field label="Not" htmlFor="gider-not">
            <input
              id="gider-not"
              type="text"
              className="input w-full"
              value={expForm.note}
              onChange={(e) => setExpForm({ ...expForm, note: e.target.value })}
            />
          </Field>

          {expError ? <ErrorNote error={expError} /> : null}
          {saveExpense.error ? <ErrorNote error={saveExpense.error} /> : null}
        </div>
      </Modal>
    </div>
  );
}

export default Finance;
